import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, Typography } from "antd";
import { FilterOutlined } from "@ant-design/icons";

import StandardGroupCriteria from "./StandardGroupCriteria.js";
import RoleGroupCriteria from "./RoleGroupCriteria.js";
import ParticipationGroupCriteria from "./ParticipationGroupCriteria.js";
import { updateGroup, selectGroupById } from "../../../actions/api";
import { types as at, errorAction } from "@authzsvc/api-lib";
import { useDispatch } from "react-redux";
import EGroupWarning from "../EGroupWarning.js";
import { showErrorNotification } from "../../../util.js";

const GroupCriteria = ({ selectedGroup, canManageGroup, isReplicaGroup }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [standardBuilderOpen, setStandardBuilderOpen] = useState(false);

  const canModifyGroup = canManageGroup && !isReplicaGroup;

  const separateCriteria = (criteria) => {
    const ROLE_KEYS = [
      "roles.role_type",
      "roles.entity_type",
      "roles.entity_id",
      "role",
      "roles.entity2_id",
    ];

    const isRoleCriterion = (criterion) =>
      criterion && typeof criterion === "object" && "role" in criterion;

    const isParticipationCriterion = (criterion) =>
      criterion &&
      typeof criterion === "object" &&
      (criterion.country ||
        criterion.institute ||
        criterion.experiment ||
        criterion.isPrimary !== undefined);

    const isParticipationGroup = (criterion) =>
      criterion &&
      (criterion.operator === "And" || criterion.operator === "Or") &&
      Array.isArray(criterion.conditions) &&
      criterion.conditions.length > 0 &&
      criterion.conditions.every(isParticipationCriterion);

    const isStandardCriterion = (criterion) =>
      criterion &&
      typeof criterion === "object" &&
      "left" in criterion &&
      !ROLE_KEYS.includes(criterion.left);

    if (!criteria)
      return {
        standardCriteria: null,
        roleCriteria: null,
        participationCriteria: null,
      };

    if (isParticipationCriterion(criteria)) {
      return {
        standardCriteria: null,
        roleCriteria: null,
        participationCriteria: criteria,
      };
    }

    if (criteria.operator === "And" && Array.isArray(criteria.conditions)) {
      const standardConditions = [];
      const roleConditions = [];
      const participationConditions = [];

      for (const cond of criteria.conditions) {
        if (isParticipationCriterion(cond)) {
          participationConditions.push(cond);
        } else if (isParticipationGroup(cond)) {
          participationConditions.push(cond);
        } else if (
          cond.operator === "Or" &&
          Array.isArray(cond.conditions) &&
          cond.conditions.every(isRoleCriterion)
        ) {
          roleConditions.push(cond);
        } else if (isStandardCriterion(cond)) {
          standardConditions.push(cond);
        } else if (
          isRoleCriterion(cond) ||
          (cond.operator === "And" &&
            Array.isArray(cond.conditions) &&
            cond.conditions.every(isRoleCriterion))
        ) {
          roleConditions.push(cond);
        } else if (
          (cond.operator === "And" || cond.operator === "Or") &&
          Array.isArray(cond.conditions)
        ) {
          standardConditions.push(cond);
        }
      }

      let roleCriteria = null;
      if (roleConditions.length === 1) {
        roleCriteria = roleConditions[0];
      } else if (roleConditions.length > 1) {
        roleCriteria = { operator: "And", conditions: roleConditions };
      }

      const standardCriteria =
        standardConditions.length === 0
          ? null
          : standardConditions.length === 1
          ? standardConditions[0]
          : { operator: "And", conditions: standardConditions };

      let participationCriteria = null;
      if (participationConditions.length === 1) {
        participationCriteria = participationConditions[0];
      } else if (participationConditions.length > 1) {
        participationCriteria = {
          operator: "And",
          conditions: participationConditions,
        };
      }

      return { standardCriteria, roleCriteria, participationCriteria };
    }

    if (
      (criteria.operator === "Or" &&
        Array.isArray(criteria.conditions) &&
        criteria.conditions.every(isRoleCriterion)) ||
      isRoleCriterion(criteria)
    ) {
      return {
        standardCriteria: null,
        roleCriteria: criteria,
        participationCriteria: null,
      };
    }

    // Top-level Or that is not all-role → standard criteria (from AND/OR builder)
    if (criteria.operator === "Or" && Array.isArray(criteria.conditions)) {
      return {
        standardCriteria: criteria,
        roleCriteria: null,
        participationCriteria: null,
      };
    }

    if (isStandardCriterion(criteria)) {
      return {
        standardCriteria: criteria,
        roleCriteria: null,
        participationCriteria: null,
      };
    }

    return {
      standardCriteria: null,
      roleCriteria: null,
      participationCriteria: null,
    };
  };

  const mergeCriteria = (standard, role, participation) => {
    const allCriteria = [];

    if (standard) {
      const standardConds =
        standard.operator === "And" && Array.isArray(standard.conditions)
          ? standard.conditions
          : [standard];
      allCriteria.push(...standardConds);
    }

    if (role) {
      allCriteria.push(role);
    }

    if (participation) {
      const participationConds =
        participation.operator === "And" &&
        Array.isArray(participation.conditions)
          ? participation.conditions
          : [participation];
      allCriteria.push(...participationConds);
    }

    if (allCriteria.length === 0) return null;
    if (allCriteria.length === 1) return allCriteria[0];

    return {
      operator: "And",
      conditions: allCriteria,
    };
  };

  const { standardCriteria, roleCriteria, participationCriteria } =
    useMemo(() => {
      return separateCriteria(selectedGroup.criteriaNew);
    }, [selectedGroup.criteriaNew]);

  // Operators: only read from backend if the criteria is a group (has a conditions array).
  // A single leaf criterion has an operator field too (e.g. "Eq"), so we must not use that.
  const standardOperator = Array.isArray(standardCriteria?.conditions)
    ? standardCriteria.operator
    : "And";
  const roleOperator = Array.isArray(roleCriteria?.conditions)
    ? roleCriteria.operator
    : "Or";
  const participationOperator = Array.isArray(participationCriteria?.conditions)
    ? participationCriteria.operator
    : "And";

  const handleUpdate = async (
    values,
    isRoleCriteria = false,
    isParticipationCriteria = false
  ) => {
    setLoading(true);
    let mergedCriteria;

    if (isParticipationCriteria) {
      mergedCriteria = mergeCriteria(standardCriteria, roleCriteria, values);
    } else if (isRoleCriteria) {
      mergedCriteria = mergeCriteria(
        standardCriteria,
        values,
        participationCriteria
      );
    } else {
      mergedCriteria = mergeCriteria(
        values,
        roleCriteria,
        participationCriteria
      );
    }

    const updatedGroup = { ...selectedGroup, criteriaNew: mergedCriteria };
    const result = await dispatch(updateGroup(updatedGroup.id, updatedGroup));

    if (result.type === errorAction(at.UPDATE_GROUP)) {
      showErrorNotification(
        result?.payload?.title || "Failed to update group",
        result?.payload?.data?.message || "Sorry, something went wrong"
      );
    }
    await dispatch(selectGroupById(updatedGroup.id));

    setLoading(false);
  };

  const handleStandardOperatorChange = (operator) => {
    if (!standardCriteria) return;
    const newStandard = Array.isArray(standardCriteria.conditions)
      ? { ...standardCriteria, operator }
      : { operator, conditions: [standardCriteria] };
    handleUpdate(newStandard);
  };

  const handleRoleOperatorChange = (operator) => {
    if (!roleCriteria) return;
    const newRole = Array.isArray(roleCriteria.conditions)
      ? { ...roleCriteria, operator }
      : { operator, conditions: [roleCriteria] };
    handleUpdate(newRole, true);
  };

  const handleParticipationOperatorChange = (operator) => {
    if (!participationCriteria) return;
    const newParticipation = Array.isArray(participationCriteria.conditions)
      ? { ...participationCriteria, operator }
      : { operator, conditions: [participationCriteria] };
    handleUpdate(newParticipation, false, true);
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <FilterOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Criteria
        </Typography.Title>
      }
    >
      <EGroupWarning
        selectedGroup={selectedGroup}
        canManageGroup={canManageGroup}
        showSyncMessage={false}
      />
      <StandardGroupCriteria
        canModifyGroup={canModifyGroup}
        standardCriteria={standardCriteria}
        handleUpdate={handleUpdate}
        loading={loading}
        categoryOperator={standardOperator}
        onCategoryOperatorChange={handleStandardOperatorChange}
        builderModalOpen={standardBuilderOpen}
        onBuilderModalClose={() => setStandardBuilderOpen(false)}
        onBuilderModalOpen={() => setStandardBuilderOpen(true)}
      />
      <RoleGroupCriteria
        canModifyGroup={canModifyGroup}
        roleCriteria={roleCriteria}
        handleUpdate={handleUpdate}
        loading={loading}
        categoryOperator={roleOperator}
        onCategoryOperatorChange={handleRoleOperatorChange}
      />
      <ParticipationGroupCriteria
        canModifyGroup={canModifyGroup}
        participationCriteria={participationCriteria}
        handleUpdate={handleUpdate}
        loading={loading}
        categoryOperator={participationOperator}
        onCategoryOperatorChange={handleParticipationOperatorChange}
      />
    </Card>
  );
};

GroupCriteria.propTypes = {
  selectedGroup: PropTypes.object,
  canManageGroup: PropTypes.bool,
};

export default GroupCriteria;
